/*
  Diamond Art ME: <dam-facet> drill renderer.
  Renders the picture inside the element at real kit scale: one 2.5 mm resin drill
  per cell (160 drills across = a 40 cm wide kit), reduced to the kit's colour
  limit (200) with median cut, each drill drawn pixel by pixel with faceted bevels
  and a grout line. A raking light then picks out per-drill glints.

  Markup:
    <dam-facet data-cols="160" data-colors="200" data-sample-src="..." data-intro="load|activate|none">
      <img ...>   visible fallback and LCP image
    </dam-facet>
  Any element with [data-dam-count] inside the closest [data-dam-viewer] shows the
  number of drills placed.

  Respects prefers-reduced-motion. Falls back to the plain <img> if the canvas
  cannot read the image.
*/
(function () {
  if (customElements.get('dam-facet')) return;

  var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  var GROUT = [27, 20, 28];
  // Facet brightness multipliers: top, left, table, right, bottom (keys are the tile codes).
  var FACET = { 40: 1.28, 18: 1.12, 6: 1.04, '-26': 0.8, '-46': 0.62 };

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function mulberry(seed) {
    return function () {
      seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
      var t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  /* Median cut to at most maxColors. Returns a palette index per pixel.
     Boxes that cannot split (a single colour) are marked done so the loop always ends. */
  function medianCut(px, n, maxColors) {
    var idx = new Int32Array(n);
    for (var i = 0; i < n; i++) idx[i] = i;
    function stats(box) {
      var lo = [255, 255, 255], hi = [0, 0, 0];
      for (var k = box.s; k < box.e; k++) {
        var p = idx[k] * 3;
        for (var c = 0; c < 3; c++) { var v = px[p + c]; if (v < lo[c]) lo[c] = v; if (v > hi[c]) hi[c] = v; }
      }
      var r = [hi[0] - lo[0], hi[1] - lo[1], hi[2] - lo[2]];
      box.ch = r[0] >= r[1] && r[0] >= r[2] ? 0 : r[1] >= r[2] ? 1 : 2;
      box.range = r[box.ch];
      box.score = box.range * (box.e - box.s);
      box.done = box.range === 0 || box.e - box.s < 2;
      return box;
    }
    var boxes = [stats({ s: 0, e: n })];
    while (boxes.length < maxColors) {
      var bi = -1, best = -1;
      for (var b = 0; b < boxes.length; b++) if (!boxes[b].done && boxes[b].score > best) { best = boxes[b].score; bi = b; }
      if (bi < 0) break;
      var box = boxes[bi], ch = box.ch;
      var seg = Array.prototype.slice.call(idx.subarray(box.s, box.e));
      seg.sort(function (a, c2) { return px[a * 3 + ch] - px[c2 * 3 + ch]; });
      for (var k = 0; k < seg.length; k++) idx[box.s + k] = seg[k];
      var mid = box.s + (seg.length >> 1);
      boxes.splice(bi, 1, stats({ s: box.s, e: mid }), stats({ s: mid, e: box.e }));
    }
    var palette = [], map = new Uint16Array(n);
    boxes.forEach(function (bx, pi) {
      var r = 0, g = 0, bl = 0, cnt = bx.e - bx.s;
      for (var k = bx.s; k < bx.e; k++) { var p = idx[k] * 3; r += px[p]; g += px[p + 1]; bl += px[p + 2]; map[idx[k]] = pi; }
      palette.push([Math.round(r / cnt), Math.round(g / cnt), Math.round(bl / cnt)]);
    });
    return { palette: palette, map: map };
  }

  /* Resin drills are glossy and saturated: lift saturation and contrast of each palette colour. */
  function resinColour(c) {
    var r = c[0] / 255, g = c[1] / 255, b = c[2] / 255;
    var mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2, h = 0, s = 0, d = mx - mn;
    if (d) {
      s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
      h = mx === r ? (g - b) / d + (g < b ? 6 : 0) : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
      h /= 6;
    }
    s = Math.min(1, s * 1.45 + 0.04);
    l = clamp(0.5 + (l - 0.5) * 1.12, 0.04, 0.96);
    function hue(p, q, t) {
      if (t < 0) t += 1; if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    }
    if (!s) return [l * 255, l * 255, l * 255];
    var q2 = l < 0.5 ? l * (1 + s) : l + s - l * s, p2 = 2 * l - q2;
    return [hue(p2, q2, h + 1 / 3) * 255, hue(p2, q2, h) * 255, hue(p2, q2, h - 1 / 3) * 255];
  }

  /* Per-pixel shade offsets for one drill of size c (device px). -999 marks grout.
     The drill is a flat table with four bevelled facets, lit from the top-left. */
  function drillTile(c) {
    var t = new Int16Array(c * c), gap = c >= 4 ? 1 : 0, d = c - gap, b = Math.max(1, Math.round(d * 0.24));
    for (var v = 0; v < c; v++) for (var u = 0; u < c; u++) {
      var o;
      if (u >= d || v >= d) o = -999;
      else {
        var top = v, left = u, right = d - 1 - u, bottom = d - 1 - v;
        var m = Math.min(top, left, right, bottom);
        if (m >= b) o = 6;
        else if (m === top) o = 40;
        else if (m === left) o = 18;
        else if (m === right) o = -26;
        else o = -46;
      }
      t[v * c + u] = o;
    }
    return { t: t, d: d, b: b };
  }

  class DamFacet extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.img = this.querySelector('img');
      if (!this.img || !window.CanvasRenderingContext2D) return;

      this.canvas = document.createElement('canvas');
      this.canvas.className = 'dam-facet__canvas';
      this.canvas.setAttribute('aria-hidden', 'true');
      this.appendChild(this.canvas);
      this.ctx = this.canvas.getContext('2d');

      this.light = { x: 0.3, y: 0.26 };
      this.target = { x: 0.3, y: 0.26 };
      this.pointerInside = false;
      this.visible = false;
      this.introDone = false;
      this.introMode = this.dataset.intro || 'load';
      this.t0 = performance.now();

      this._tick = this.tick.bind(this);
      this._onScroll = this.onScroll.bind(this);
      this.addEventListener('pointermove', this.onMove.bind(this));
      this.addEventListener('pointerleave', () => { this.pointerInside = false; });
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this.addEventListener('dam:activate', () => this.activate());

      this.io = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.startLoop();
      }, { threshold: 0.05 });
      this.io.observe(this);

      var resizeTimer;
      this.ro = new ResizeObserver(() => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => this.build(false), 160);
      });

      this.loadSample().then((sample) => {
        this.sample = sample;
        this.build(this.introMode !== 'none' && !reduceQuery.matches);
        this.ro.observe(this);
      }).catch(() => { this.classList.add('dam-facet--fallback'); });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      if (this.io) this.io.disconnect();
      if (this.ro) this.ro.disconnect();
      cancelAnimationFrame(this.raf);
      this._connected = false;
    }

    loadSample() {
      var src = this.dataset.sampleSrc || this.img.currentSrc || this.img.src;
      return new Promise((resolve, reject) => {
        var im = new Image();
        im.crossOrigin = 'anonymous';
        im.decoding = 'async';
        im.onload = () => {
          try {
            var c = document.createElement('canvas');
            c.width = im.naturalWidth; c.height = im.naturalHeight;
            var x = c.getContext('2d');
            x.drawImage(im, 0, 0);
            x.getImageData(0, 0, 1, 1); // throws if tainted
            resolve(c);
          } catch (e) { reject(e); }
        };
        im.onerror = reject;
        im.src = src;
      });
    }

    counters() {
      var root = this.closest('[data-dam-viewer]') || this;
      return root.querySelectorAll('[data-dam-count]');
    }
    setCount(n) {
      var s = n.toLocaleString('en-US');
      this.counters().forEach(function (el) { el.textContent = s; });
    }

    build(withIntro) {
      var W = this.offsetWidth, H = this.offsetHeight; // layout size, ignores entrance transforms
      if (!W || !H || !this.sample) return;
      if (this._w === W && this._h === H && this.base) return;
      this._w = W; this._h = H;

      var dpr = Math.min(window.devicePixelRatio || 1, 3);
      var cols = parseInt(this.dataset.cols || 160, 10);
      var ar = parseFloat(this.dataset.ar);
      var rows = Math.max(1, Math.round(ar > 0 ? cols / ar : cols * H / W));
      var cpx = Math.max(3, Math.round((W * dpr) / cols)); // device px per drill, integer for crisp facets
      var CW = cols * cpx, CH = rows * cpx;
      this.grid = { cols: cols, rows: rows, c: cpx, CW: CW, CH: CH };
      this.canvas.width = CW; this.canvas.height = CH;

      // Cover-crop, then average down to one sample per drill (3x supersample first).
      var s = this.sample, sw = s.width, sh = s.height, boxAR = cols / rows, cx, cy, cw, ch;
      if (sw / sh > boxAR) { ch = sh; cw = sh * boxAR; cx = (sw - cw) / 2; cy = 0; }
      else { cw = sw; ch = sw / boxAR; cx = 0; cy = (sh - ch) * 0.5; }
      var mid = document.createElement('canvas'); mid.width = cols * 3; mid.height = rows * 3;
      var mx = mid.getContext('2d'); mx.imageSmoothingQuality = 'high';
      mx.drawImage(s, cx, cy, cw, ch, 0, 0, mid.width, mid.height);
      var small = document.createElement('canvas'); small.width = cols; small.height = rows;
      var sx = small.getContext('2d'); sx.imageSmoothingQuality = 'high';
      sx.drawImage(mid, 0, 0, cols, rows);
      var raw = sx.getImageData(0, 0, cols, rows).data;
      var n = cols * rows, px = new Uint8Array(n * 3);
      for (var i = 0; i < n; i++) { px[i * 3] = raw[i * 4]; px[i * 3 + 1] = raw[i * 4 + 1]; px[i * 3 + 2] = raw[i * 4 + 2]; }

      var q = medianCut(px, n, parseInt(this.dataset.colors || 200, 10));
      var vivid = q.palette.map(resinColour);
      var rand = mulberry(cols * 7919 + rows);
      var tile = drillTile(cpx);

      var baseImg = new ImageData(CW, CH), hiImg = new ImageData(CW, CH);
      var B = baseImg.data, Hd = hiImg.data, tt = tile.t, d = tile.d, bev = tile.b;
      for (var r = 0; r < rows; r++) {
        for (var cc = 0; cc < cols; cc++) {
          var di = r * cols + cc, col = vivid[q.map[di]];
          var glint = 0.55 + rand() * 0.45;
          var shimmer = rand() < 0.03;
          // Only some drills throw a hot spot at any one angle; at small sizes a glint on every drill reads as grey.
          var sparkle = cpx >= 7 || rand() < 0.18;
          var hue = shimmer ? rand() * 360 : 0;
          var ox = cc * cpx, oy = r * cpx;
          for (var v = 0; v < cpx; v++) {
            var row = (oy + v) * CW;
            for (var u = 0; u < cpx; u++) {
              var o = tt[v * cpx + u], p = (row + ox + u) * 4;
              if (o === -999) {
                // Grout takes a deep shade of the neighbouring drill, so colour is not diluted by black lines.
                B[p] = col[0] * 0.38; B[p + 1] = col[1] * 0.38; B[p + 2] = col[2] * 0.38; B[p + 3] = 255; continue;
              }
              var m = FACET[o], add = o === 40 ? 10 : 0;
              B[p] = clamp(col[0] * m + add, 0, 255); B[p + 1] = clamp(col[1] * m + add, 0, 255); B[p + 2] = clamp(col[2] * m + add, 0, 255); B[p + 3] = 255;
              // Specular pass: glossy top facet and a bright hot spot near the top-left of the table.
              var a = 0;
              if (o === 40) a = 0.32 * glint;
              else if (o === 18) a = 0.12 * glint;
              if (sparkle && u >= bev && u <= bev + Math.max(0, Math.round(d * 0.18)) && v >= bev && v <= bev + Math.max(0, Math.round(d * 0.1))) a = glint;
              if (shimmer && o !== -999) {
                // Fairy-dust accent: an iridescent sheen across the whole drill.
                var hh = (hue + u * 18 + v * 12) % 360, k = hh / 60, f = k - Math.floor(k), sec = Math.floor(k) % 6;
                var rgb = [[1, f, 0], [1 - f, 1, 0], [0, 1, f], [0, 1 - f, 1], [f, 0, 1], [1, 0, 1 - f]][sec];
                Hd[p] = 150 + rgb[0] * 105; Hd[p + 1] = 150 + rgb[1] * 105; Hd[p + 2] = 150 + rgb[2] * 105;
                Hd[p + 3] = Math.max(a, 0.7) * 255;
              } else if (a > 0) {
                Hd[p] = 255; Hd[p + 1] = 249; Hd[p + 2] = 238; Hd[p + 3] = a * 255;
              }
            }
          }
        }
      }
      this.base = document.createElement('canvas'); this.base.width = CW; this.base.height = CH;
      this.base.getContext('2d').putImageData(baseImg, 0, 0);
      this.highlight = document.createElement('canvas'); this.highlight.width = CW; this.highlight.height = CH;
      this.highlight.getContext('2d').putImageData(hiImg, 0, 0);
      this.lightLayer = this.lightLayer || document.createElement('canvas');
      this.lightLayer.width = CW; this.lightLayer.height = CH;
      this.total = n;
      this.twinkles = null;

      this.classList.add('dam-facet--ready');

      var introPending = this.delays && !this.introDone;
      if ((withIntro || introPending) && !this.introDone) this.prepareIntro(rand);
      else { this.introDone = true; this.settled = null; this.setCount(n); }

      if (reduceQuery.matches) { this.light.x = this.target.x = 0.3; this.light.y = this.target.y = 0.26; }
      this.draw(performance.now());
      this.startLoop();
    }

    prepareIntro(rand) {
      var g = this.grid, n = g.cols * g.rows;
      var split = this.dataset.split != null ? clamp(parseFloat(this.dataset.split) || 0.5, 0, 0.95) : null;
      // Placement order: a diagonal sweep with a ragged edge, the way a kit fills in.
      this.delays = new Float32Array(n);
      for (var r = 0; r < g.rows; r++) for (var q = 0; q < g.cols; q++) {
        var xn = q / g.cols;
        if (split != null) xn = clamp((xn - split) / (1 - split), 0, 1);
        this.delays[r * g.cols + q] = (xn * 0.55 + (r / g.rows) * 0.45) * 1500 + rand() * 260;
      }
      this.order = new Uint32Array(n);
      for (var i = 0; i < n; i++) this.order[i] = i;
      var dl = this.delays;
      this.order.sort(function (a, b) { return dl[a] - dl[b]; });
      this.placed = 0;
      this.settled = document.createElement('canvas');
      this.settled.width = g.CW; this.settled.height = g.CH;
      this.settledCtx = this.settled.getContext('2d');
      this.settledCtx.fillStyle = 'rgb(' + GROUT.join(',') + ')';
      this.settledCtx.fillRect(0, 0, g.CW, g.CH);
      this.introStart = null;
      this.introDone = false;
      this.introArmed = this.introMode === 'load' || !!this.pendingActivate;
      this.setCount(0);
    }

    activate() {
      if (this.introDone || reduceQuery.matches) return;
      if (!this.settled) { this.pendingActivate = true; return; }
      this.introArmed = true;
      this.introStart = null;
      this.startLoop();
    }

    onMove(e) {
      if (!finePointer.matches) return;
      var r = this.getBoundingClientRect();
      this.target.x = clamp((e.clientX - r.left) / r.width, -0.2, 1.2);
      this.target.y = clamp((e.clientY - r.top) / r.height, -0.2, 1.2);
      this.pointerInside = true;
      this.startLoop();
    }

    onScroll() {
      if (finePointer.matches || !this.visible) return;
      var r = this.getBoundingClientRect();
      this.scrollLight = clamp(1 - (r.top + r.height) / (window.innerHeight + r.height), 0, 1);
      this.startLoop();
    }

    startLoop() {
      if (this.running || !this.base || !this.visible) return;
      if (reduceQuery.matches && this.introDone) { this.draw(performance.now()); return; }
      this.running = true;
      this.raf = requestAnimationFrame(this._tick);
    }

    tick(now) {
      this.running = false;
      if (!this.visible || document.hidden) return;
      var paused = this.closest('.is-photo');
      // Idle drift runs at half rate; nothing draws while the picture view is showing.
      if (paused || (!this.pointerInside && this.introDone && (this._skip = !this._skip))) {
        this.running = true;
        this.raf = requestAnimationFrame(this._tick);
        return;
      }
      if (!this.pointerInside) {
        var t = (now - this.t0) / 1000;
        this.target.x = 0.5 + Math.sin(t * 0.42) * 0.4;
        this.target.y = (this.scrollLight != null ? 0.15 + this.scrollLight * 0.7 : 0.4) + Math.sin(t * 0.31 + 1.2) * 0.24;
      }
      this.light.x += (this.target.x - this.light.x) * 0.075;
      this.light.y += (this.target.y - this.light.y) * 0.075;
      this.draw(now);
      this.running = true;
      this.raf = requestAnimationFrame(this._tick);
    }

    draw(now) {
      var ctx = this.ctx, g = this.grid, cw = g.CW, ch = g.CH;
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;

      var front = null;
      if (!this.introDone && this.settled) {
        if (!this.introArmed) { ctx.drawImage(this.settled, 0, 0); return; }
        if (this.introStart == null) this.introStart = now;
        front = this.placeDrills(now - this.introStart);
        ctx.drawImage(this.settled || this.base, 0, 0);
      } else {
        ctx.drawImage(this.base, 0, 0);
      }

      var l = this.lightLayer.getContext('2d');
      l.globalCompositeOperation = 'source-over';
      l.clearRect(0, 0, cw, ch);
      l.drawImage(this.highlight, 0, 0);
      l.globalCompositeOperation = 'destination-in';

      if (front != null) {
        // During placement the glints ride the leading edge of the sweep.
        var sp = this.dataset.split != null ? clamp(parseFloat(this.dataset.split) || 0.5, 0, 0.95) : 0;
        var sw0 = cw * (1 - sp), al = 0.55 / sw0, be = 0.45 / ch, lam = 1 / (al * al + be * be);
        var lg = l.createLinearGradient(cw * sp, 0, cw * sp + lam * al, lam * be);
        var f = clamp(front - 0.09, 0, 1);
        lg.addColorStop(clamp(f - 0.08, 0, 1), 'rgba(0,0,0,0)');
        lg.addColorStop(clamp(f - 0.01, 0, 1), 'rgba(0,0,0,1)');
        lg.addColorStop(clamp(f + 0.01, 0, 1), 'rgba(0,0,0,0)');
        l.fillStyle = lg;
        l.fillRect(0, 0, cw, ch);
        ctx.globalCompositeOperation = 'lighter';
        ctx.drawImage(this.lightLayer, 0, 0);
        ctx.globalCompositeOperation = 'source-over';
        return;
      }

      var lx = this.light.x * cw, ly = this.light.y * ch, rad = Math.max(cw, ch) * 0.38;
      var grad = l.createRadialGradient(lx, ly, 0, lx, ly, rad);
      grad.addColorStop(0, 'rgba(0,0,0,1)');
      grad.addColorStop(0.4, 'rgba(0,0,0,0.7)');
      grad.addColorStop(1, 'rgba(0,0,0,0.22)');
      l.fillStyle = grad;
      l.fillRect(0, 0, cw, ch);

      // Gentle raking falloff away from the lamp.
      var fall = ctx.createRadialGradient(lx, ly, rad * 0.2, lx, ly, rad * 2.4);
      fall.addColorStop(0, 'rgb(255,255,255)');
      fall.addColorStop(1, 'rgb(226,218,230)');
      ctx.globalCompositeOperation = 'multiply';
      ctx.fillStyle = fall;
      ctx.fillRect(0, 0, cw, ch);

      ctx.globalCompositeOperation = 'lighter';
      ctx.drawImage(this.lightLayer, 0, 0);
      if (!reduceQuery.matches) this.drawTwinkles(now);
      ctx.globalCompositeOperation = 'source-over';
    }

    /* A few drills at a time flash a four-point star, like resin catching light as you move. */
    drawTwinkles(now) {
      var g = this.grid, ctx = this.ctx, c = g.c;
      var sp = this.dataset.split != null ? clamp(parseFloat(getComputedStyle(this).getPropertyValue('--dam-split')) || parseFloat(this.dataset.split) || 0.5, 0, 0.95) : 0;
      if (!this.twinkles) {
        var count = Math.round(g.cols * g.rows * 0.0016), rnd = mulberry(g.cols + g.rows * 31);
        this.twinkles = [];
        for (var i = 0; i < count; i++) this.twinkles.push({ x: rnd(), y: rnd(), t0: rnd() * 2600, dur: 1400 + rnd() * 1600, rnd: rnd });
      }
      for (var k = 0; k < this.twinkles.length; k++) {
        var tw = this.twinkles[k], ph = ((now + tw.t0) % tw.dur) / tw.dur;
        if (ph < 0.02 && !tw.moved) { tw.x = tw.rnd(); tw.y = tw.rnd(); tw.moved = true; }
        if (ph > 0.5) tw.moved = false;
        var a = Math.pow(Math.sin(ph * Math.PI), 6);
        if (a < 0.03) continue;
        var px = sp * g.CW + tw.x * g.CW * (1 - sp), py = tw.y * g.CH;
        px = Math.floor(px / c) * c + c * 0.35; py = Math.floor(py / c) * c + c * 0.3;
        var len = c * (1.6 + 1.4 * a), w = Math.max(1, c * 0.22);
        ctx.globalAlpha = a;
        ctx.fillStyle = 'rgb(255,252,240)';
        ctx.fillRect(px - len, py - w / 2, len * 2, w);
        ctx.fillRect(px - w / 2, py - len, w, len * 2);
        ctx.fillRect(px - w, py - w, w * 2, w * 2);
      }
      ctx.globalAlpha = 1;
    }

    /* Commits every drill whose time has come, in placement order. Returns the sweep front 0..1. */
    placeDrills(elapsed) {
      var g = this.grid, c = g.c, n = g.cols * g.rows, sctx = this.settledCtx;
      while (this.placed < n && this.delays[this.order[this.placed]] <= elapsed) {
        var i = this.order[this.placed++], q = i % g.cols, r = (i / g.cols) | 0;
        sctx.drawImage(this.base, q * c, r * c, c, c, q * c, r * c, c, c);
      }
      this.setCount(this.placed);
      if (this.placed >= n) {
        this.introDone = true;
        this.settled = null;
        this.dispatchEvent(new CustomEvent('dam:placed', { bubbles: true }));
        return null;
      }
      return elapsed / 1500;
    }
  }

  customElements.define('dam-facet', DamFacet);

  // Before/after divider: drag, tap or use arrow keys. --dam-split lives on [data-dam-split-root].
  function setSplit(root, handle, v) {
    v = clamp(v, 0.04, 0.96);
    root.style.setProperty('--dam-split', v.toFixed(4));
    handle.setAttribute('aria-valuenow', String(Math.round(v * 100)));
    handle.setAttribute('aria-valuetext', Math.round(v * 100) + '% picture');
  }
  document.addEventListener('pointerdown', function (e) {
    var handle = e.target.closest('[data-dam-split-handle]');
    if (!handle) return;
    var root = handle.closest('[data-dam-split-root]');
    if (!root) return;
    e.preventDefault();
    handle.setPointerCapture(e.pointerId);
    handle.classList.add('is-dragging');
    function move(ev) {
      var r = root.getBoundingClientRect();
      setSplit(root, handle, (ev.clientX - r.left) / r.width);
    }
    function up() {
      handle.classList.remove('is-dragging');
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
    }
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
  });
  document.addEventListener('keydown', function (e) {
    var handle = e.target.closest && e.target.closest('[data-dam-split-handle]');
    if (!handle) return;
    var root = handle.closest('[data-dam-split-root]');
    var cur = parseFloat(getComputedStyle(root).getPropertyValue('--dam-split')) || 0.5;
    var step = e.shiftKey ? 0.1 : 0.05;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') cur -= step;
    else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') cur += step;
    else if (e.key === 'Home') cur = 0;
    else if (e.key === 'End') cur = 1;
    else return;
    e.preventDefault();
    setSplit(root, handle, cur);
  });

  // Photo / diamond art toggles.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-dam-view]');
    if (!btn) return;
    var root = btn.closest('[data-dam-viewer]');
    if (!root) return;
    var view = btn.getAttribute('data-dam-view');
    root.classList.toggle('is-photo', view === 'photo');
    root.querySelectorAll('[data-dam-view]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b === btn));
    });
  });
})();
